import React, { useEffect, useState } from 'react'
import { Editor } from '@monaco-editor/react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Props = {
  language?: string
  value: string
  initialValue?: string
  onChange: (code: string) => void
  onSubmit: () => void
  submitting?: boolean
  hideSubmit?: boolean
  showHaveQuestions?: boolean
  onHaveQuestions?: () => void
  disabled?: boolean
  disabledReason?: string
  readOnly?: boolean
  question?: string
}

const CodeExercise: React.FC<Props> = ({
  language = 'plaintext',
  value,
  initialValue,
  onChange,
  onSubmit,
  submitting = false,
  hideSubmit = false,
  showHaveQuestions = false,
  onHaveQuestions,
  disabled = false,
  disabledReason = 'AI is speaking, please wait.',
  readOnly = false,
  question,
}) => {
  const displayLanguage = language && language.trim() !== '' ? language : 'plaintext'
  const isEdited = (value?.trim() || '') !== (initialValue?.trim() || '') && Boolean(value?.trim())
  const effectiveReadOnly = readOnly || disabled
  const [fontSize, setFontSize] = useState(9);
  const [showQuestionTooltip, setShowQuestionTooltip] = useState(false);

  useEffect(() => {
    const updateFontSize = () => {
      const width = window.innerWidth;
      if (width >= 1280) {
        setFontSize(13.5); // xl
      } else if (width >= 1024) {
        setFontSize(11); // lg
      } else {
        setFontSize(9); // md and below
      }
    };

    updateFontSize();
    window.addEventListener('resize', updateFontSize);
    return () => window.removeEventListener('resize', updateFontSize);
  }, []);

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 md:py-1.5 md:px-2 lg:py-2 lg:px-2.5 xl:py-2 xl:px-4 bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
        <div>
          <TooltipProvider delayDuration={50}>
            <Tooltip open={showQuestionTooltip} onOpenChange={setShowQuestionTooltip}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setShowQuestionTooltip(true)}
                  onMouseEnter={() => setShowQuestionTooltip(true)}
                  onMouseLeave={() => setShowQuestionTooltip(false)}
                  className="md:text-[9px] lg:text-[12px] xl:text-[14px] leading-relaxed text-gray-700 font-medium cursor-help hover:text-gray-900 transition-colors duration-150"
                >
                  Show Question
                </button>
              </TooltipTrigger>
              {question && (
                <TooltipContent side="right" align="end" className="max-w-md">
                  <p className="md:text-[9px] lg:text-[12px] xl:text-[14px] text-sm md:leading-[1.75] lg:leading-[1.75] xl:leading-[1.75]">{question}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2">
          {showHaveQuestions && (
            <button
              type="button"
              onClick={() => {
                if (disabled) return
                onHaveQuestions && onHaveQuestions()
              }}
              disabled={disabled}
              className="px-3 py-1.5 md:px-1.5 md:py-0 lg:px-2 lg:py-0.5 xl:px-2.5 xl:py-1 bg-transparent text-black rounded-sm border border-slate-700 disabled:opacity-60 disabled:cursor-not-allowed md:text-[9px] lg:text-[11px] xl:text-[13.5px] text-sm"
            >
              Have Question
            </button>
          )}
          {!hideSubmit && (
            <button
              type="button"
              onClick={() => {
                if (disabled) return
                onSubmit()
              }}
              disabled={disabled || submitting || !isEdited}
              className="px-3 py-1.5 md:px-1.5 md:py-0 lg:px-2 lg:py-0.5 xl:px-2.5 xl:py-1 bg-button text-white rounded-sm border border-button disabled:opacity-60 disabled:cursor-not-allowed md:text-[9px] lg:text-[11px] xl:text-[13.5px] text-sm"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          )}
        </div>
      </div>
      <div
        className="bg-white flex-1 min-h-0 px-4 relative"
        title={disabled ? disabledReason : undefined}
      >
        {/* <span className="md:text-[9px] lg:text-[12px] xl:text-[14px] leading-relaxed text-gray-700 font-medium ">
          {displayLanguage}
        </span> */}
        <Editor
          value={value}
          language={displayLanguage}
          theme="light"
          options={{
            readOnly: effectiveReadOnly,
            domReadOnly: effectiveReadOnly,
            padding: { top: 16, bottom: 16 },
            minimap: { enabled: false },
            lineNumbers: "off",
            lineNumbersMinChars: 0,
            lineDecorationsWidth: 0,
            glyphMargin: false,
            folding: false,
            scrollBeyondLastLine: false,
            renderLineHighlight: "none",
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
            },
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace",
            fontSize: fontSize,
            lineHeight: 1.6,
            selectionHighlight: false,
            occurrencesHighlight: 'off',
            cursorStyle: 'line-thin',
            cursorBlinking: 'solid',
            renderValidationDecorations: 'off',
            quickSuggestions: false,
            parameterHints: { enabled: false },
            suggestOnTriggerCharacters: false,
            acceptSuggestionOnEnter: 'off',
            tabCompletion: 'off',
            wordBasedSuggestions: 'off',
            selectionClipboard: false,
            contextmenu: false,
            mouseWheelZoom: false,
            links: false,
            hover: { enabled: false },

          }}
          onChange={(v) => {
            if (effectiveReadOnly) return
            onChange(v || '')
          }}
          height="100%"
        />
      </div>
    </div>
  )
}

export default CodeExercise
